import React, { useState } from 'react'
import Input from 'uiKit/inputs/Input'
import Select from 'uiKit/Select/Select'
import Checkbox from 'uiKit/Checkbox'
import TextareaAutosize from 'react-textarea-autosize'
import DeleteIcon from '@material-ui/icons/Delete'
import AddIcon from '@material-ui/icons/Add'
import { alertError } from '../../../../../api'
import {
  QuestionContainer,
  QuestionHeader,
  QuestionNumber,
  DeleteButton,
  QuestionContent,
  QuestionInputWrapper,
  QuestionTypeWrapper,
  RequiredCheckboxWrapper,
  FlexContainer,
  OptionsContainer,
  OptionsHeader,
  OptionItem,
  OptionNumber,
  OptionInputWrapper,
  DeleteOptionButton,
  AddOptionButton,
  CorrectAnswerContainer,
  CorrectAnswerHeader,
  CorrectAnswerSelect,
  ValidationError,
} from './QuestionItemStyle'

const MAX_TEXT_LENGTH = 2048

interface QuestionItemProps {
  index: number
  onDelete?: () => void
  onChange?: (questionData: QuestionData) => void
  questionData: QuestionData
  disabled?: boolean
}

export interface QuestionData {
  id: string
  question: string
  type: string
  isRequired: boolean
  options?: Array<{ id: string; value: string }>
  correctAnswers?: string[] // Array of option ids that are correct answers
  minCorrectOptions?: number // Minimum number of correct options that must be selected
  _validationError?: string // Temporary field for validation errors
  // Inline validation flags (drive conditional error messages in UI)
  errors?: {
    questionText?: boolean
    optionErrors?: Record<string, boolean>
    correctAnswers?: boolean
    minSelection?: boolean
  }
}

const QuestionItem: React.FC<QuestionItemProps> = ({ index, onDelete, onChange, questionData, disabled }) => {
  const [isCorrectAnswerMenuOpen, setIsCorrectAnswerMenuOpen] = useState(false)
  const [isMinCorrectOptionsMenuOpen, setIsMinCorrectOptionsMenuOpen] = useState(false)
  const handleQuestionChange = (e: any) => {
    // Handle both direct value and event object
    const incoming = e && e.target ? e.target.value : e
    if (typeof incoming === 'string' && incoming.length > MAX_TEXT_LENGTH) {
      alertError(`Question cannot exceed ${MAX_TEXT_LENGTH} characters`)
      return
    }
    const trimmed = typeof incoming === 'string' ? incoming.trim() : incoming
    const updatedData = {
      ...questionData,
      question: incoming,
      errors: {
        ...questionData.errors,
        questionText: !trimmed,
      },
    }
    onChange && onChange(updatedData)
  }

  const handleTypeChange = (selectedOption: any) => {
    if (!selectedOption) return

    const updatedData = {
      ...questionData,
      type: selectedOption.value,
      // Reset irrelevant errors when type changes
      errors:
        selectedOption.value === 'free_text'
          ? { ...questionData.errors, correctAnswers: false, minSelection: false, optionErrors: {} }
          : { ...questionData.errors },
    }
    onChange && onChange(updatedData)
  }

  const handleRequiredChange = (checked: boolean) => {
    const updatedData = {
      ...questionData,
      isRequired: checked,
    }
    onChange && onChange(updatedData)
  }

  const handleOptionChange = (optionId: string, value: string) => {
    if (typeof value === 'string' && value.length > MAX_TEXT_LENGTH) {
      alertError(`Option cannot exceed ${MAX_TEXT_LENGTH} characters`)
      return
    }
    if (!questionData.options) return

    const updatedOptions = questionData.options.map(option => (option.id === optionId ? { ...option, value } : option))
    const updatedData = {
      ...questionData,
      options: updatedOptions,
      errors: {
        ...questionData.errors,
        optionErrors: {
          ...(questionData.errors?.optionErrors || {}),
          [optionId]: !value || value.trim() === '',
        },
      },
    }
    onChange && onChange(updatedData)
  }

  const handleAddOption = () => {
    if (!questionData.options) return

    // Check if any existing options are empty
    const hasEmptyOptions = questionData.options.some(option => !option.value || option.value.trim() === '')

    if (hasEmptyOptions) {
      // Show error message using the parent component's validation system
      if (onChange) {
        // Mark all empty options with inline errors so the UI shows per-option errors
        const updatedOptionErrors: Record<string, boolean> = {
          ...(questionData.errors?.optionErrors || {}),
        }
        questionData.options.forEach(opt => {
          const isEmpty = !opt.value || opt.value.trim() === ''
          if (isEmpty) {
            updatedOptionErrors[opt.id] = true
          }
        })

        onChange({
          ...questionData,
          _validationError: 'Please fill in all existing options before adding a new one',
          errors: {
            ...questionData.errors,
            optionErrors: updatedOptionErrors,
          },
        })
      }
      return
    }

    // All options have values, proceed with adding a new one
    const newOption = {
      id: `option_${questionData.options.length + 1}_${Date.now()}`,
      value: '',
    }

    const updatedData = {
      ...questionData,
      options: [...questionData.options, newOption],
      // Clear any previous validation error
      _validationError: undefined,
      errors: {
        ...questionData.errors,
        optionErrors: {
          ...(questionData.errors?.optionErrors || {}),
          [newOption.id]: true, // new option is empty -> show inline error until user fills it
        },
      },
    }
    onChange && onChange(updatedData)
  }

  const handleDeleteOption = (optionId: string) => {
    if (!questionData.options || questionData.options.length <= 2) return

    // Remove the option
    const updatedOptions = questionData.options.filter(option => option.id !== optionId)

    // Also remove this option from correctAnswers if it was selected
    const updatedCorrectAnswers = questionData.correctAnswers
      ? questionData.correctAnswers.filter(id => id !== optionId)
      : []

    // Check if we removed a correct answer option
    const wasCorrectAnswer = questionData.correctAnswers?.includes(optionId)

    // Adjust minCorrectOptions if needed
    let updatedMinCorrectOptions = questionData.minCorrectOptions
    // If we removed a correct answer or if minCorrectOptions is greater than available correct answers
    if (wasCorrectAnswer && updatedMinCorrectOptions > updatedCorrectAnswers.length) {
      updatedMinCorrectOptions = updatedCorrectAnswers.length || 1
    }

    const updatedData = {
      ...questionData,
      options: updatedOptions,
      correctAnswers: updatedCorrectAnswers,
      minCorrectOptions: updatedMinCorrectOptions,
      errors: {
        ...questionData.errors,
        optionErrors: {
          ...(questionData.errors?.optionErrors || {}),
          [optionId]: undefined as unknown as boolean, // remove error entry for deleted option
        },
        // Recompute minSelection error if needed
        minSelection:
          updatedCorrectAnswers && updatedCorrectAnswers.length > 0
            ? !updatedMinCorrectOptions ||
              updatedMinCorrectOptions < 1 ||
              (questionData.options && updatedMinCorrectOptions > updatedOptions.length)
            : false,
      },
    }
    onChange && onChange(updatedData)
  }

  const handleCorrectAnswerChange = (selectedOptions: any) => {
    if (!selectedOptions) return

    const correctAnswers = Array.isArray(selectedOptions)
      ? selectedOptions.map(option => option.value)
      : [selectedOptions.value]

    // If the current minCorrectOptions is greater than the number of selected correct answers,
    // adjust it to match the new count
    const minCorrectOptions =
      questionData.minCorrectOptions && questionData.minCorrectOptions > correctAnswers.length
        ? correctAnswers.length
        : questionData.minCorrectOptions

    const updatedData = {
      ...questionData,
      correctAnswers,
      minCorrectOptions,
      errors: {
        ...questionData.errors,
        correctAnswers: correctAnswers.length === 0,
        minSelection:
          correctAnswers.length > 0
            ? !minCorrectOptions ||
              minCorrectOptions < 1 ||
              (questionData.options && (minCorrectOptions as number) > correctAnswers.length)
            : false,
      },
    }
    onChange && onChange(updatedData)
  }

  const handleMinCorrectOptionsChange = (selectedOption: any) => {
    if (!selectedOption) return

    const updatedData = {
      ...questionData,
      minCorrectOptions: parseInt(selectedOption.value, 10),
      errors: {
        ...questionData.errors,
        minSelection: !selectedOption?.value,
      },
    }
    onChange && onChange(updatedData)
  }

  const questionTypeOptions = [
    { value: 'free_text', label: 'Free text' },
    { value: 'multiple_choice', label: 'Multiple choice' },
  ]

  // Create options for the correct answer select
  const correctAnswerOptions = questionData.options
    ? questionData.options
        .filter(option => option.value.trim() !== '')
        .map(option => ({
          value: option.id,
          label: option.value,
        }))
    : []

  return (
    <QuestionContainer>
      <QuestionHeader>
        <QuestionNumber>Question {index}</QuestionNumber>
        <FlexContainer>
          {/* <RequiredCheckboxWrapper>
            <Checkbox label="Make as mandatory" checked={questionData.isRequired} onChange={handleRequiredChange} />
          </RequiredCheckboxWrapper> */}
          <QuestionTypeWrapper>
            <Select
              options={questionTypeOptions}
              styles={{
                control: base => ({
                  ...base,
                  border: '1px solid rgba(226, 232, 234, 1)',
                  borderRadius: '8px',
                  outline: 'none',
                  boxShadow: 'none',
                  '&:hover': {
                    border: '1px solid rgba(226, 232, 234, 1)',
                    boxShadow: 'none',
                  },
                }),
              }}
              value={questionTypeOptions.find(option => option.value === questionData.type)}
              onChange={handleTypeChange}
              isDisabled={disabled}
              placeholder="Select answer type"
            />
          </QuestionTypeWrapper>
          {disabled ? null : (
            <DeleteButton onClick={onDelete}>
              <DeleteIcon />
            </DeleteButton>
          )}
        </FlexContainer>
      </QuestionHeader>
      <QuestionContent>
        <QuestionInputWrapper>
          <p>Question</p>
          <TextareaAutosize
            disabled={disabled}
            maxLength={MAX_TEXT_LENGTH}
            placeholder="Enter the question"
            value={questionData.question}
            className="textarea-autogrow"
            style={{ backgroundColor: disabled ? '#f5f5f5' : undefined }}
            onChange={handleQuestionChange}
          />
          {questionData.errors?.questionText ? <ValidationError>Question cannot be empty.</ValidationError> : null}
        </QuestionInputWrapper>

        {questionData.type === 'multiple_choice' && questionData.options && (
          <>
            <OptionsContainer>
              <OptionsHeader>Options</OptionsHeader>
              {questionData.options.map((option, optionIndex) => (
                <OptionItem key={option.id}>
                  <OptionNumber>{(optionIndex + 1).toString().padStart(2, '0')}.</OptionNumber>
                  <OptionInputWrapper>
                    <TextareaAutosize
                      disabled={disabled}
                      maxLength={MAX_TEXT_LENGTH}
                      placeholder="Enter option"
                      value={option.value}
                      className="textarea-autogrow"
                      style={{ backgroundColor: disabled ? '#f5f5f5' : undefined }}
                      onChange={e => handleOptionChange(option.id, e.target.value)}
                    />
                    {questionData.errors?.optionErrors?.[option.id] ? (
                      <ValidationError>The option cannot be empty</ValidationError>
                    ) : null}
                  </OptionInputWrapper>
                  {disabled
                    ? null
                    : questionData.options &&
                      questionData.options.length > 2 && (
                        <DeleteOptionButton onClick={() => handleDeleteOption(option.id)}>
                          <DeleteIcon fontSize="default" style={{ fontSize: '18px' }} />
                        </DeleteOptionButton>
                      )}
                  {disabled
                    ? null
                    : questionData.options &&
                      questionData.options.length === optionIndex + 1 && (
                        <AddOptionButton onClick={handleAddOption}>
                          <AddIcon fontSize="default" style={{ fontSize: '18px' }} />
                        </AddOptionButton>
                      )}
                </OptionItem>
              ))}
            </OptionsContainer>
            <FlexContainer>
              <CorrectAnswerContainer>
                <CorrectAnswerHeader>Correct answer</CorrectAnswerHeader>
                <CorrectAnswerSelect>
                  <Select
                    options={correctAnswerOptions}
                    isMulti
                    placeholder="Select the correct answer(s)"
                    value={correctAnswerOptions.filter(option => questionData.correctAnswers?.includes(option.value))}
                    onChange={handleCorrectAnswerChange}
                    isDisabled={disabled}
                    menuPortalTarget={document.body}
                    closeMenuOnScroll={e => {
                      const target = (e && (e.target as HTMLElement)) || null
                      if (target && (target.closest('.cb-select__menu') || target.closest('.cb-select__menu-list'))) {
                        // Scrolling inside the dropdown menu -> keep it open
                        return false
                      }
                      if (isCorrectAnswerMenuOpen) setIsCorrectAnswerMenuOpen(false)
                      return true
                    }}
                    onMenuOpen={() => setIsCorrectAnswerMenuOpen(true)}
                    onMenuClose={() => setIsCorrectAnswerMenuOpen(false)}
                    styles={{
                      control: base => ({
                        ...base,
                        border: '1px solid rgba(226, 232, 234, 1)',
                        borderRadius: '8px',
                        outline: 'none',
                        boxShadow: 'none',
                        '&:hover': {
                          border: '1px solid rgba(226, 232, 234, 1)',
                          boxShadow: 'none',
                        },
                      }),
                    }}
                  />
                </CorrectAnswerSelect>
                {questionData.errors?.correctAnswers ? (
                  <ValidationError>Select the correct option(s)</ValidationError>
                ) : null}
              </CorrectAnswerContainer>

              {/* Minimum Correct Options Select - only show if there are correct answers selected */}
              {questionData.correctAnswers && questionData.correctAnswers.length > 0 && (
                <CorrectAnswerContainer>
                  <CorrectAnswerHeader>Maximum choice selection</CorrectAnswerHeader>
                  <CorrectAnswerSelect>
                    <Select
                      options={Array.from({ length: questionData.correctAnswers.length }, (_, i) => ({
                        value: String(i + 1),
                        label: String(i + 1),
                      }))}
                      isDisabled={disabled}
                      placeholder="Select minimum required"
                      menuPortalTarget={document.body}
                      closeMenuOnScroll={e => {
                        const target = (e && (e.target as HTMLElement)) || null
                        if (target && (target.closest('.cb-select__menu') || target.closest('.cb-select__menu-list'))) {
                          // Scrolling inside the dropdown menu -> keep it open
                          return false
                        }
                        if (isMinCorrectOptionsMenuOpen) setIsMinCorrectOptionsMenuOpen(false)
                        return true
                      }}
                      onMenuOpen={() => setIsMinCorrectOptionsMenuOpen(true)}
                      onMenuClose={() => setIsMinCorrectOptionsMenuOpen(false)}
                      value={
                        questionData.minCorrectOptions
                          ? {
                              value: String(questionData.minCorrectOptions),
                              label: String(questionData.minCorrectOptions),
                            }
                          : null
                      }
                      onChange={handleMinCorrectOptionsChange}
                      styles={{
                        control: base => ({
                          ...base,
                          border: '1px solid rgba(226, 232, 234, 1)',
                          borderRadius: '8px',
                          outline: 'none',
                          boxShadow: 'none',
                          '&:hover': {
                            border: '1px solid rgba(226, 232, 234, 1)',
                            boxShadow: 'none',
                          },
                        }),
                      }}
                    />
                  </CorrectAnswerSelect>
                  {questionData.errors?.minSelection ? (
                    <ValidationError>Select maximum choice selection</ValidationError>
                  ) : null}
                </CorrectAnswerContainer>
              )}
            </FlexContainer>
          </>
        )}
      </QuestionContent>
    </QuestionContainer>
  )
}

export default React.memo(QuestionItem)
